import React from 'react';

import BlockContent from '~/components/layouts/block-content';
import LoadingIndicator from '~/components/loading-indicator';
import AlertMessage from '~/components/messages/alert-message';
import LazyImg from '~/components/lazy-img';
import Modal from '~/components/modals/modal';
import ProductBadge from '~/components/products-grid/products-cell-badge';
import ProductCarousels from '~/components/products/product-detail/product-carousels';
import Price from '~/components/modals/product-details/price';
import { ReviewStars } from '~/components/static/smart-lawn-plan/review';
import { useProductBySku } from '~/features/products/products-actions';
import { useMediaQuery } from '~/hooks/use-media-query';
import { useYotpo } from '~/hooks/use-yotpo';
import { productContentSerializers } from '~/components/modals/product-details/product-content-serializers';

import styles from '~/components/modals/account/boxes/box-product-details-modal.module.scss';
import modalStyles from '~/components/modals/modal.module.scss';
import carouselStyles from '~/components/products/product-detail/product-carousel.module.scss';

const BoxProductDetailsModal = ({
  isOpen,
  onClose,
  skuId,
  quantity = 1,
  cartUuid,
}) => {
  if (!skuId || !cartUuid) {
    return null;
  }

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      className={modalStyles.productDetail}
    >
      <BoxProductDetailsModalContent
        skuId={skuId}
        onClose={onClose}
        quantity={quantity}
        cartUuid={cartUuid}
      />
    </Modal>
  );
};

const BoxProductDetailsModalContent = ({
  skuId,
  onClose,
  quantity, // qtySelected will be used when adding to box/subscription
  cartUuid, // cartUuid will be used when adding to box/subscription
}) => {
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const { product, isLoading, isError } = useProductBySku(skuId);

  const images = [];
  if (product?.primaryImage) {
    images.push(product.primaryImage);
  }
  if (product?.carouselImages) {
    images.push(...product.carouselImages);
  }

  const descriptionCopy = product?.pdpFlexContent || product?.shortDescription;

  const { averageRating, totalReviews } = useYotpo(
    product?.reviewSkuOverride || product?.sku
  );

  if (isLoading) {
    return (
      <div
        className={modalStyles.loadingIndicator}
        data-testid="loading-indicator"
      >
        <h1>Loading product info</h1>
        <LoadingIndicator />
      </div>
    );
  }
  if (isError || (!isLoading && !product)) {
    return (
      <div className={modalStyles.errorMessage} data-testid="error-message">
        <AlertMessage heading="Something went wrong">
          There was an unexpected problem, please try again later or contact CS
          if you need to make changes to your box.
        </AlertMessage>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {isDesktop ? (
        <DesktopView
          product={product}
          images={images}
          totalReviews={totalReviews}
          averageRating={averageRating}
          descriptionCopy={descriptionCopy}
        />
      ) : (
        <MobileView
          product={product}
          images={images}
          descriptionCopy={descriptionCopy}
          totalReviews={totalReviews}
          averageRating={averageRating}
        />
      )}
    </div>
  );
};

export default BoxProductDetailsModal;

const DesktopView = ({
  product,
  images,
  totalReviews,
  averageRating,
  descriptionCopy,
}) => {
  return (
    <section className={styles.product}>
      <div className={carouselStyles.productModal}>
        <ProductBadge
          fullPrice={product?.fullPrice}
          productDetails={{
            inStock: product?.inStock,
            customBadge: product?.customBadge,
            pdpDisplayType: product?.pdpDisplayType,
          }}
          unitPrice={product?.unitPrice}
        />
        <ProductCarousels images={images} videos={product?.videos} />
      </div>

      <div className={styles.detailsColumn}>
        <h2 className={styles.name}>{product?.name}</h2>
        <Price
          fullPrice={product?.fullPrice}
          inStock={product?.inStock}
          unitPrice={product?.unitPrice}
        />
        <p className={styles.formFactor}>{product?.formFactor}</p>
        {totalReviews > 0 && (
          <div className={styles.reviews}>
            <ReviewStars
              rating={averageRating}
              className={styles.reviewStars}
            />{' '}
            ({totalReviews})
          </div>
        )}

        <div className={styles.productDescription}>
          <BlockContent
            content={descriptionCopy}
            serializers={productContentSerializers}
          />
        </div>
        <div className={styles.freeShipping}>
          <LazyImg
            src="/icons/closed-package-icon-filled-alt.svg"
            alt="Free shipping!"
            className={styles.freeShippingIcon}
            height="44"
            width="44"
          />
          <p className={styles.freeShippingText}>Free shipping!</p>
        </div>
        <div className={styles.addToWrapper}>
          {/* qty input here */}
          {/* add to box/subscription checkboxes here */}
          {/* add to box/subscription button here */}
        </div>
      </div>
    </section>
  );
};

const MobileView = ({
  product,
  images,
  descriptionCopy,
  totalReviews,
  averageRating,
}) => {
  return (
    <section className={styles.product}>
      <>
        <h2 className={styles.name}>{product?.name}</h2>
        <p className={styles.formFactor}>{product?.formFactor}</p>
        {totalReviews > 0 && (
          <div className={styles.reviews}>
            <ReviewStars
              rating={averageRating}
              className={styles.reviewStars}
            />{' '}
            ({totalReviews})
          </div>
        )}

        <div className={carouselStyles.productModal}>
          <ProductBadge
            fullPrice={product?.fullPrice}
            productDetails={{
              inStock: product?.inStock,
              customBadge: product?.customBadge,
              pdpDisplayType: product?.pdpDisplayType,
            }}
            unitPrice={product?.unitPrice}
          />
          <ProductCarousels images={images} videos={product.videos} />
        </div>
        <Price
          fullPrice={product?.fullPrice}
          inStock={product?.inStock}
          unitPrice={product?.unitPrice}
        />

        <div className={styles.addToWrapper}>
          {/* qty input here */}
          {/* add to box/subscription checkboxes here */}
          {/* add to box/subscription button here */}
        </div>

        {product?.tagLine && (
          <p className={styles.tagLine}>
            {product.tagLine} <span className={styles.tagLineDash} />
          </p>
        )}
        <div className={styles.productDescription}>
          <BlockContent
            content={descriptionCopy}
            serializers={productContentSerializers}
          />
        </div>
      </>
    </section>
  );
};
